import * as React from 'react';
import { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { FaTimes } from 'react-icons/fa';
import { v4 as uuidv4 } from 'uuid';
import Message from '../Message';
import ChatInput from '../ChatInput';
import SessionSidebar from '../SessionSidebar';
import LocalStorageManager from '../../services/storageManager';
import ChatbotApiClient from '../../services/apiClient';
import contentService from '../../services/contentService';
import lightLogo from '../../assets/light-mode/logo-short.png';
import type { ChatMessage, ChatSession, ChatbotProps } from '../../types';
import './ChatInterface.scss';

export interface ChatInterfaceProps extends ChatbotProps {
  setIsVisible?: (visible: boolean) => void;
  setIsMinimized?: (minimized: boolean) => void;
}

const ChatInterface: React.FC<ChatInterfaceProps> = ({
  mode,
  apiBaseUrl,
  enableMockMode = false,
  useProxy,
  placeholder = contentService.ui.placeholders.messageInput(),
  welcomeMessage = contentService.messages.welcome(),
  maxMessages = 100,
  onSessionChange,
  onError,
  setIsVisible,
  setIsMinimized,
}) => {
  // Theme state: fixed to light only
  const [theme] = useState<'light'>('light');
  const [currentSession, setCurrentSession] = useState<ChatSession | null>(null);
  const [sessions, setSessions] = useState<ChatSession[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  // Ensure sidebar starts closed on mount
  useEffect(() => {
    setIsSidebarOpen(false);
  }, []);

  // useEffect(()=>{
  //   if (currentSession) {
  //     handleSendMessage("Get my profile");
  //   }
  // },[currentSession])

  // Defensive: force sidebar closed after loading sessions
  useEffect(() => {
    setIsSidebarOpen(false);
  }, [sessions]);
  const [error, setError] = useState<string | null>(null);
  const [streamingMessageId, setStreamingMessageId] = useState<string | null>(null);
  const [hasReceivedFirstResponse, setHasReceivedFirstResponse] = useState<boolean>(false);
  
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const storageManager = useRef(new LocalStorageManager());
  const apiClient = useRef(new ChatbotApiClient({
    apiBaseUrl, 
    enableMockMode,
    useProxy
  }));
  const isUserScrollingRef = useRef(false);
  const lastScrollTimeRef = useRef(0);

  // Enhanced scroll to bottom with observer pattern
  const scrollToBottom = useCallback((force: boolean = false) => {
    if (isUserScrollingRef.current && !force) return;

    const scrollToEnd = () => {
      if (messagesContainerRef.current) {
        const container = messagesContainerRef.current;
        const isAtBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 100;
        
        if (isAtBottom || force) {
          // Use both methods for maximum compatibility
          container.scrollTop = container.scrollHeight;
          
          if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({
              behavior: force ? 'auto' : 'smooth', // Instant scroll during streaming
              block: 'end'
            });
          }
        }
      }
    };

    // For streaming updates, scroll immediately
    if (force) {
      scrollToEnd();
    } else {
      // Use multiple frames to ensure DOM is ready for regular scrolling
      requestAnimationFrame(() => {
        requestAnimationFrame(scrollToEnd);
      });
    }
  }, []);

  // Detect user scrolling to prevent auto-scroll interference
  const handleScroll = useCallback(() => {
    if (messagesContainerRef.current) {
      const container = messagesContainerRef.current;
      const isAtBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 50;
      isUserScrollingRef.current = !isAtBottom;
    }
  }, []);

  // Auto-scroll when messages change or loading state changes (immediate, no delay)
  useLayoutEffect(() => {
    if (currentSession?.messages.filter(m => !m.hidden).length || isLoading) {
      scrollToBottom(true); // Force scroll for new visible messages and loading state
    }
  }, [currentSession?.messages, isLoading, scrollToBottom]);

  // Load sessions on mount
  useEffect(() => {
    const storedSessions = storageManager.current.getSessions()
      .map(stored => {
        const session = storageManager.current.hydrateChatSession(stored);
        // Mark any existing "Hi" messages as hidden for backward compatibility
        const updatedMessages = session.messages.map(message => {
          if (message.role === 'user' && message.content === 'Hi') {
            return { ...message, hidden: true };
          }
          return message;
        });
        return { ...session, messages: updatedMessages };
      });
    setSessions(storedSessions);
    
    // Save updated sessions back to storage to persist hidden flag changes
    storedSessions.forEach(session => {
      storageManager.current.saveSession(session);
    });
    
    // Start with empty session if no sessions exist to show WelcomeMessage
    if (storedSessions.length === 0) {
      const sessionId = uuidv4();
      const now = new Date();
      
      // Create session with no messages to trigger WelcomeMessage component
      const messages: ChatMessage[] = [];

      const newSession: ChatSession = {
        id: sessionId,
        title: contentService.metadata.sessionTitles.newChat(),
        messages,
        createdAt: now,
        updatedAt: now,
      };

      setCurrentSession(newSession);
      setSessions([newSession]);
      storageManager.current.saveSession(newSession);
      setHasReceivedFirstResponse(false);
    } else {
      // Load the most recent session (with updated hidden flags)
      const mostRecentSession = storedSessions[0];
      setCurrentSession(mostRecentSession);
      
      // Check if this session already has received the first response
      // (has at least one assistant message that's not hidden)
      const hasAssistantResponse = mostRecentSession.messages.some(
        msg => msg.role === 'assistant' && !msg.hidden
      );
      setHasReceivedFirstResponse(hasAssistantResponse);
    }
  }, [welcomeMessage]);

  // Notify parent of session changes
  useEffect(() => {
    if (currentSession && onSessionChange) {
      onSessionChange(currentSession);
    }
  }, [currentSession, onSessionChange]);

  const createNewSession = useCallback(() => {
    const sessionId = uuidv4();
    const now = new Date();
    
    // Always create session with empty messages to show WelcomeMessage component
    const messages: ChatMessage[] = [];

    const newSession: ChatSession = {
      id: sessionId,
      title: contentService.metadata.sessionTitles.newChat(),
      messages,
      createdAt: now,
      updatedAt: now,
    };

    setCurrentSession(newSession);
    setSessions(prev => [newSession, ...prev]);
    storageManager.current.saveSession(newSession);
    setIsSidebarOpen(false);
    setHasReceivedFirstResponse(false); // Reset for new session
  }, []);

  const updateCurrentSession = useCallback((updater: (session: ChatSession) => ChatSession) => {
    setCurrentSession(prevSession => {
      if (!prevSession) return prevSession;
      
      const updatedSession = updater(prevSession);
      
      // Update sessions list
      setSessions(prev => prev.map(s => s.id === updatedSession.id ? updatedSession : s));
      
      // Save to storage
      storageManager.current.saveSession(updatedSession);
      
      return updatedSession;
    });
  }, []); // Remove currentSession dependency

  const handleSendMessage = useCallback(async (content: string) => {
    if (!currentSession) {
      setError(contentService.errors.chat.noActiveSession());
      return;
    }

    setIsLoading(true);
    setError(null);

    // Check if this is the automatic "Hi" message
    const isAutoHiMessage = content === "Hi";

    const userMessage: ChatMessage = {
      id: uuidv4(),
      role: 'user',
      content: content,
      timestamp: new Date(),
      hidden: isAutoHiMessage, // Hide "Hi" messages from UI
    };

    // Add user message immediately
    updateCurrentSession(session => {
      const newSession = {
        ...session,
        messages: [...session.messages, userMessage],
        updatedAt: new Date(),
        title: session.title === contentService.metadata.sessionTitles.newChat() || session.title === contentService.metadata.sessionTitles.welcome() 
          ? storageManager.current.generateSessionTitle(content)
          : session.title,
      };
      
      // Limit messages if needed
      if (newSession.messages.length > maxMessages) {
        newSession.messages = newSession.messages.slice(-maxMessages);
      }
      
      return newSession;
    });

    // Small delay to ensure UI updates before API call
    await new Promise(resolve => setTimeout(resolve, 50));

    try {

      // Use the OAuth-enabled API client for OneAI chat (actual backend)
      const chatResponse = await apiClient.current.sendMessage({
        message: content,
        sessionId: currentSession.id
      });

      // Handle both string and structured component responses
      const responseContent = chatResponse.response;
      // No need to convert to string - leave structured components as objects

      const assistantMessage: ChatMessage = {
        id: uuidv4(),
        role: 'assistant',
        content: responseContent, // Keep as-is (string or structured component)
        timestamp: new Date(),
        hidden: false, // Always show assistant responses, even for "Hi"
      };

      
      // Set this message as streaming (except for welcome messages)
      // Only compare string content for welcome message check
      const isWelcomeMessage = typeof assistantMessage.content === 'string' && assistantMessage.content === welcomeMessage;
      if (!isWelcomeMessage) {
        setStreamingMessageId(assistantMessage.id);
      }
      
      updateCurrentSession(session => {
        const newSession = {
          ...session,
          messages: [...session.messages, assistantMessage],
          updatedAt: new Date(),
        };
        return newSession;
      });

      // If this was the first API call (from "Hi"), add a static welcome message
      if (isAutoHiMessage && !hasReceivedFirstResponse) {
        setHasReceivedFirstResponse(true);
        
        // Add a static welcome message after a short delay
        setTimeout(() => {
          const staticWelcomeMessage: ChatMessage = {
            id: uuidv4(),
            role: 'assistant',
            content: {
              componentType: 'welcomeMessage',
              content: {}
            },
            timestamp: new Date(),
            hidden: false,
          };

          updateCurrentSession(session => {
            const newSession = {
              ...session,
              messages: [...session.messages, staticWelcomeMessage],
              updatedAt: new Date(),
            };
            return newSession;
          });
        }, 500); // Small delay to ensure the first response is rendered first
      }

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to send message';
      setError(errorMessage);
      onError?.(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [currentSession, updateCurrentSession, maxMessages, onError, welcomeMessage]);

  // Auto-send "Hi" for new sessions
  useEffect(() => {
    if (currentSession && currentSession.messages.length === 0) {
      // This is a new session, send "Hi" automatically
      const timer = setTimeout(() => {
        handleSendMessage("Hi");
      }, 100);
      
      return () => clearTimeout(timer);
    }
  }, [currentSession, handleSendMessage]);

  // Handle streaming completion
  const handleStreamComplete = useCallback(() => {
    setStreamingMessageId(null);
  }, []);

  // Handle streaming text updates for auto-scroll
  const handleStreamUpdate = useCallback(() => {
    // Throttle scroll updates during streaming to avoid excessive calls
    const now = Date.now();
    if (now - lastScrollTimeRef.current > 100) { // Max 10 scrolls per second
      lastScrollTimeRef.current = now;
      scrollToBottom(true);
    }
  }, [scrollToBottom]);

  const handleSessionSelect = useCallback((sessionId: string) => {
    const session = sessions.find(s => s.id === sessionId);
    if (session) {
      setCurrentSession(session);
      setIsSidebarOpen(false);
      
      // Check if this session already has received the first response
      const hasAssistantResponse = session.messages.some(
        msg => msg.role === 'assistant' && !msg.hidden
      );
      setHasReceivedFirstResponse(hasAssistantResponse);
    }
  }, [sessions]);

  const handleDeleteSession = useCallback((sessionId: string) => {
    storageManager.current.deleteSession(sessionId);
    setSessions(prev => prev.filter(s => s.id !== sessionId));
    
    if (currentSession?.id === sessionId) {
      const remainingSessions = sessions.filter(s => s.id !== sessionId);
      if (remainingSessions.length > 0) {
        setCurrentSession(remainingSessions[0]);
      } else {
        createNewSession();
      }
    }
  }, [sessions, currentSession, createNewSession]);

  return (
  <div className={`chat-interface theme-${theme} ${mode === 'widget' ? 'chat-interface--widget' : 'chat-interface--standalone'}${isSidebarOpen ? ' chat-interface--with-sidebar' : ''}`}>
      {/* Overlay to close sidebar on outside click */}
      {isSidebarOpen && (
        <div
          style={{
            position: 'absolute',
            left: 0,
            top: 0,
            width: '100%',
            height: '100%',
            zIndex: 999,
            background: 'rgba(0,0,0,0.01)',
          }}
          onClick={() => setIsSidebarOpen(false)}
        />
      )}
      {/* Sidebar */}
      <SessionSidebar
  sessions={sessions}
  currentSessionId={currentSession?.id || null}
  onSessionSelect={handleSessionSelect}
  onNewSession={() => createNewSession()}
  onDeleteSession={handleDeleteSession}
  onClose={() => setIsSidebarOpen(false)}
  isOpen={isSidebarOpen}
  theme={theme}
  mode={mode ?? 'standalone'}
      />

      {/* Main Chat Area */}
      <div className="chat-interface__main">
        {/* Header */}
        <div className="chat-interface__header">
          <div className="chat-interface__header-actions">
            <button
              onClick={() => setIsSidebarOpen(!isSidebarOpen)}
              className="chat-interface__header-button"
              title={contentService.tooltips.toggleSidebar()}
            >
              {/* Custom hamburger with 2 lines */}
              <div className="hamburger-icon">
                <span className="hamburger-line"></span>
                <span className="hamburger-line"></span>
              </div>
            </button>
          </div>

          <div className="chat-interface__header-center">
            <div className="chat-interface__header-logo-group">
              <img 
                src={lightLogo}
                alt="OneAssist Logo"
                className="chat-interface__header-logo-image"
              />
              <span className="chat-interface__header-title">OneAssist</span>
            </div>
          </div>

          <div className="chat-interface__header-actions">
            {/* Widget mode: Add close and minimize buttons */}
            {mode === 'widget' && (
                <button
                  onClick={() => setIsMinimized?.(true)}
                  className="chat-interface__header-button"
                  title="Minimize chat"
                >
                  −
                </button>
            )}
            {/* Standalone mode: Only close button */}
            {mode === 'standalone' && (
              <button
                className="chat-interface__header-button"
                title={contentService.tooltips.close()}
                onClick={() => setIsVisible?.(false)}
              >
                <FaTimes size={16} />
              </button>
            )}
          </div>
        </div>

        {/* Error message */}
        {error && (
          <div className="chat-interface__error">
            <p>{error}</p>
            <button
              onClick={() => setError(null)}
            >
              {contentService.ui.buttons.clear()}
            </button>
          </div>
        )}

        {/* Content area that slides (messages + input) */}
        <div className="chat-interface__content">
          {/* Messages Area */}
          <div 
            className="chat-interface__messages" 
            ref={messagesContainerRef}
            onScroll={handleScroll}
          >
            {currentSession && currentSession.messages.length > 0 ? (
              <>
                {currentSession.messages
                  .filter(message => !message.hidden) // Filter out hidden messages
                  .map((message, index, visibleMessages) => (
                  <Message
                    key={message.id}
                    message={message}
                    isLatest={index === visibleMessages.length - 1}
                    shouldStream={message.id === streamingMessageId}
                    onStreamComplete={handleStreamComplete}
                    onStreamUpdate={handleStreamUpdate}
                    onSendMessage={handleSendMessage}
                  />
                ))}
                {/* Loading message */}
                {isLoading && (
                  <div className="chat-interface__loading-message">
                    <div className="message message--assistant">
                      <div className="message__content">
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <img 
                            src={lightLogo}
                            alt="Loading..."
                            style={{
                              width: '20px',
                              height: '20px',
                              animation: 'spin 1s linear infinite'
                            }}
                          />
                          <span style={{ fontSize: '14px', color: '#666' }}>Thinking...</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <></>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="chat-interface__input">
            <ChatInput
              onSendMessage={handleSendMessage}
              disabled={isLoading}
              placeholder={placeholder}
              loading={isLoading}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatInterface;
